import { useMemo, useState } from 'react'
import { useTranslation } from '@/i18n'
import { AlertTriangle, Bell, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MOCK_ALERTS, type AlertEntityType, type AlertItem } from './mockAlerts'

interface Props {
  search: string
  onSearchChange: (value: string) => void
  onAlertClick?: (alert: AlertItem) => void
  selectedAlertId?: string
}

const ENTITY_TYPE_OPTIONS: { value: AlertEntityType | 'all'; labelKey: string; defaultLabel: string }[] = [
  { value: 'all', labelKey: 'knowledge_graph:alerts.entity_types.all', defaultLabel: 'All types' },
  { value: 'enterprise', labelKey: 'knowledge_graph:alerts.entity_types.enterprise', defaultLabel: 'Enterprise' },
  { value: 'site', labelKey: 'knowledge_graph:alerts.entity_types.site', defaultLabel: 'Site' },
  { value: 'operating_area', labelKey: 'knowledge_graph:alerts.entity_types.operating_area', defaultLabel: 'Operating Area' },
  { value: 'documents', labelKey: 'knowledge_graph:alerts.entity_types.documents', defaultLabel: 'Documents' },
  { value: 'system', labelKey: 'knowledge_graph:alerts.entity_types.system', defaultLabel: 'System' },
  { value: 'component', labelKey: 'knowledge_graph:alerts.entity_types.component', defaultLabel: 'Component' },
  { value: 'operation_unit', labelKey: 'knowledge_graph:alerts.entity_types.operation_unit', defaultLabel: 'Operation Unit' },
  { value: 'functional_location', labelKey: 'knowledge_graph:alerts.entity_types.functional_location', defaultLabel: 'Functional Location' },
]

const AlertView = ({ search, onSearchChange, onAlertClick, selectedAlertId }: Props) => {
  const { t } = useTranslation()
  const [entityType, setEntityType] = useState<AlertEntityType | 'all'>('all')

  const alerts = useMemo(() => {
    const q = search.trim().toLowerCase()
    return MOCK_ALERTS.filter((a) => entityType === 'all' || a.entityType === entityType).filter(
      (a) =>
        q
          ? a.entity.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)
          : true,
    )
  }, [entityType, search])

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Sub-toolbar */}
      <div className="flex h-[60px] items-center justify-between gap-3 border-b border-border px-6 shrink-0">
        <div className="relative w-[240px]">
          <Search className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={String(t('knowledge_graph:toolbar.search', { defaultValue: 'Search' }))}
            className="pl-8 h-9"
          />
        </div>
        <Select value={entityType} onValueChange={(v) => setEntityType(v as AlertEntityType | 'all')}>
          <SelectTrigger className="w-[240px] h-9">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                {t('knowledge_graph:alerts.entity_types_label', { defaultValue: 'Entity types:' })}
              </span>
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            {ENTITY_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey, { defaultValue: opt.defaultLabel })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* List or empty state */}
      <div className="flex-1 min-h-0 overflow-auto">
        {alerts.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6">
            <div className="flex flex-col items-center gap-4 rounded-lg p-6">
              <div className="flex size-8 items-center justify-center rounded-lg bg-muted">
                <Bell className="size-4 text-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground text-center">
                {t('knowledge_graph:alerts.empty.title', { defaultValue: 'No alerts yet.' })}
              </p>
            </div>
          </div>
        ) : (
          <ul className="flex flex-col">
            {alerts.map((alert) => {
              const selected = alert.id === selectedAlertId
              const tone =
                alert.severity === 'critical'
                  ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
                  : 'bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400'
              return (
                <li key={alert.id}>
                  <button
                    type="button"
                    onClick={() => onAlertClick?.(alert)}
                    className={`flex w-full items-start gap-3 px-6 py-3 text-left transition-colors hover:bg-muted/50 ${
                      selected ? 'bg-muted' : ''
                    }`}
                  >
                    <span
                      className={`mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full border ${tone}`}
                    >
                      <AlertTriangle className="size-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground">{alert.entity}</p>
                      <p className="text-xs text-muted-foreground truncate">{alert.description}</p>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export default AlertView
